// src/components/features/pixels/PixelCard.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plug, Loader2, Copy, Check, Code } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { StorageUtils } from '@/utils/storage';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { auth } from '@/services/firebase';

export function PixelCard({ pixel }) {
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showSnippet, setShowSnippet] = useState(false);
  const isAdmin = StorageUtils.isAdmin();
  const queryClient = useQueryClient();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleToggleStatus = async () => {
    if (!isAdmin || isUpdating) return;

    setIsUpdating(true);
    setError(null);

    try {
      const token = await auth.currentUser?.getIdToken();
      await axios.patch(
        `/api/pixels/${pixel.id}`,
        {
          isActive: !pixel.isActive,
          tier: pixel.tier,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      await queryClient.invalidateQueries({ queryKey: ['pixels'] });
    } catch (err) {
      console.error('Error updating pixel status:', err);
      setError('Failed to update pixel status');
    } finally {
      setIsUpdating(false);
    }
  };

  const getInstallationSnippet = () => {
    return `<!-- Start SourceLink Pixel -->
<script src="https://cdn.jsdelivr.net/gh/sourcelinkapp/source-link-app-pixel@latest/sourcelink.js?pixelId=${pixel.id}"></script>
<!-- End SourceLink Pixel -->`;
  };

  const handleCopySnippet = async () => {
    try {
      await navigator.clipboard.writeText(getInstallationSnippet());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleManageIntegrations = () => {
    console.log('Navigating to integrations with pixel:', pixel.id);
    navigate('/integrations', {
      state: { pixelId: pixel.id },
      replace: false, // Ensure we're adding to history, not replacing
    });
  };

  return (
    <div
      className="sl-pixel-card rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-8"
      data-pixel-id={pixel.id}
      data-pixel-tier={pixel.tier}
      data-pixel-status={pixel.isActive ? 'active' : 'inactive'}>
      <div className="sl-pixel-header flex items-center justify-between mb-3">
        <h3 className="sl-pixel-id text-sm font-medium text-gray-900">
          Pixel ID{' '}
          <code className="text-purple-600 bg-purple-50 ml-2 px-2 py-1 rounded">
            {pixel.id}
          </code>
        </h3>
        <div className="sl-pixel-controls flex items-center gap-3">
          {isAdmin && (
            <div className="sl-pixel-toggle flex items-center gap-2">
              {isUpdating ? (
                <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
              ) : (
                <Switch
                  checked={pixel.isActive}
                  onCheckedChange={handleToggleStatus}
                  className="sl-pixel-switch"
                  disabled={isUpdating}
                />
              )}
            </div>
          )}
          <Badge
            variant={pixel.isActive ? 'success' : 'secondary'}
            className="sl-pixel-status">
            {pixel.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </div>

      {error && (
        <div className="sl-error-message mb-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="sl-pixel-info space-y-6 mb-4">
        <div className="sl-pixel-metadata flex items-center justify-between text-sm">
          <span className="sl-pixel-created text-gray-600">
            Created on {formatDate(pixel.createdAt)}
          </span>
          <Badge variant="outline" className="sl-pixel-tier capitalize">
            {pixel.tier || 'free'}
          </Badge>
        </div>

        <div className="sl-pixel-integrations">
          <div className="sl-integration-header text-base text-gray-500 font-bold mb-2">
            Integrations ({pixel.integrations?.length || 0})
          </div>
          {pixel.integrations?.length > 0 ? (
            <div className="sl-integration-list flex flex-wrap gap-1">
              {pixel.integrations.map((integration) => (
                <Badge
                  key={integration.name}
                  variant="secondary"
                  className="sl-integration-badge text-xs">
                  {integration.name}
                </Badge>
              ))}
            </div>
          ) : (
            <div className="sl-no-integrations text-xs text-gray-400">
              No integrations configured
            </div>
          )}
        </div>
      </div>

      <div className="sl-pixel-actions space-y-2">
        <Button
          variant="primary"
          icon={<Plug className="h-4 w-4" />}
          fullWidth
          onClick={handleManageIntegrations}>
          Manage Integrations
        </Button>

        <Button
          variant="secondary"
          icon={<Code className="h-4 w-4" />}
          fullWidth
          onClick={() => setShowSnippet(!showSnippet)}>
          {showSnippet ? 'Hide Installation Code' : 'Show Installation Code'}
        </Button>

        {showSnippet && (
          <div className="sl-snippet-container relative mt-2">
            <div className="sl-snippet-header flex justify-between items-center bg-gray-100 px-4 py-2 rounded-t-md border border-b-0 border-gray-200">
              <span className="text-xs text-gray-600 font-medium">
                Installation Code
              </span>
              <Button
                variant="ghost"
                size="sm"
                icon={
                  copied ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )
                }
                onClick={handleCopySnippet}>
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <pre className="sl-snippet-code block bg-gray-50 p-4 rounded-b-md border border-gray-200 text-sm overflow-x-auto whitespace-pre-wrap">
              <code>{getInstallationSnippet()}</code>
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
